import { IconBuilding, IconHome, IconLocation, IconLocationPin, IconStarFilled } from '@repo/svgs';
import { MaskedText } from '@/components/ui/MaskedText';
import { cn } from '@/lib/cn';
import { SectionHeader } from '@/components/ui';

interface RatingsSectionProps {
  buildingAddress: string;
  neighborhoodName?: string;
  isBlurRequired: boolean;
  buildingRatings: any | null;
  neighborhoodRatings: any | null;
}

const nf = new Intl.NumberFormat('en-US');

function formatReviews(count?: number | null) {
  if (typeof count !== 'number') return '';
  const n = nf.format(count);
  return `${n} review${count === 1 ? '' : 's'}`;
}

function displayRating(value?: number | null) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return value.toFixed(1);
}

function RatingValue({
  value,
  count,
  isBlurRequired,
  className,
}: {
  value?: number | null;
  count?: number | null;
  isBlurRequired: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-2">
        <IconStarFilled className="w-4 h-4 icon-rating" />
        <div className="text-body-1-high text-primary">
          {isBlurRequired ? <MaskedText className="w-12 h-5" /> : displayRating(value)}
        </div>
      </div>
      <div className="text-body-2 text-secondary">
        {isBlurRequired ? <MaskedText className="w-20 h-4" /> : formatReviews(count)}
      </div>
    </div>
  );
}

const CATEGORY_ROWS: Array<{ label: string; key: string }> = [
  { label: 'Cleanliness', key: 'avgCleanliness' },
  { label: 'Trash management', key: 'avgGarbageManagement' },
  { label: 'Pest control', key: 'avgPestControl' },
  { label: 'Water pressure', key: 'avgWaterPressure' },
  { label: 'Heat', key: 'avgHeat' },
  { label: 'Neighbors', key: 'avgNeighbors' },
  { label: 'Noise levels', key: 'avgNoiseLevels' },
  { label: 'Owner responsiveness', key: 'avgOwnerResponsiveness' },
];

export function RatingsSection({
  buildingAddress,
  neighborhoodName: buildingNeighborhoodName,
  isBlurRequired,
  buildingRatings,
  neighborhoodRatings,
}: RatingsSectionProps) {
  const neighborhoodName =
    buildingNeighborhoodName || (neighborhoodRatings?.neighborhoodName as string | undefined) || '';

  return (
    <div className="w-full">
      {/* Header */}
      <SectionHeader
        title={`Building and neighborhood ratings for ${buildingAddress}`}
        subtitleText={`Openigloo analysis`}
      />
      <div className="md:px-2">
        {/* Addresses row */}
        <div className="sticky top-0 bg-primary">
          <div className="py-5 grid grid-cols-2 px-2">
            <div className="flex items-center gap-2 pr-4">
              <IconBuilding className="w-5 h-5 icon-secondary shrink-0" aria-hidden />
              <div className="text-body-2-high text-primary">{buildingAddress}</div>
            </div>
            <div className="flex items-center gap-2 border-l border-tertiary pl-4">
              <IconLocation className="w-5 h-5 icon-secondary shrink-0" aria-hidden />
              <div className="text-body-2-high text-primary">{neighborhoodName}</div>
            </div>
          </div>

          <div className="mx-2 mb-2 border-b border-secondary" />
        </div>

        {/* Overall rating */}
        <div className="py-3 px-2 text-body-2-high text-primary">Overall rating</div>
        <div className="py-4 px-2 grid grid-cols-2">
          <RatingValue
            value={buildingRatings?.avgOverallRating}
            count={buildingRatings?.reviewCount}
            isBlurRequired={isBlurRequired}
            className="pr-4"
          />
          <RatingValue
            value={neighborhoodRatings?.avgOverallRating}
            count={neighborhoodRatings?.reviewCount}
            isBlurRequired={isBlurRequired}
            className="border-l border-tertiary pl-4"
          />
        </div>

        {/* Per-category */}
        <div className="py-3 px-2 text-body-2-high text-primary">Rating per category</div>

        <div>
          {CATEGORY_ROWS.map((row, idx) => {
            const bVal = buildingRatings?.[row.key];
            const nVal = neighborhoodRatings?.[row.key];
            return (
              <div key={row.key}>
                <div className="space-y-2 px-2 py-4">
                  <div className="text-body-2 text-primary">{row.label}</div>
                  <div className="grid grid-cols-2">
                    <div>
                      <div className="flex items-center gap-2 pr-4">
                        <IconStarFilled className="w-4 h-4 icon-rating" />
                        <div className="text-body-2-high text-primary">
                          {isBlurRequired ? (
                            <MaskedText className="w-10 h-4" />
                          ) : (
                            displayRating(bVal)
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="border-l border-tertiary pl-4">
                      <div className="flex items-center gap-2">
                        <IconStarFilled className="w-4 h-4 icon-rating" />
                        <div className="text-body-2-high text-primary">
                          {isBlurRequired ? (
                            <MaskedText className="w-10 h-4" />
                          ) : (
                            displayRating(nVal)
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {idx < CATEGORY_ROWS.length - 1 ? (
                  <div className="border-b border-secondary mx-2" />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
